import React from 'react';
// eslint-disable-next-line no-restricted-imports -- This file defines the Markdown component that should be used
import ReactMarkdown from 'react-markdown';
import type { Styling } from 'src/context/styling';
import type { CSSInterpolation } from '@emotion/serialize';
import SiteConsumer from '../../context/site-consumer';
import { parseIfImageUrl } from 'src/helpers/hosted-asset-helpers';
import remarkGfm from 'remark-gfm';
import rehypeExternalLinks from 'rehype-external-links';
import type { UrlTransform } from 'react-markdown/lib';

type MarkdownProps = {
  markdownText: string;
  openLinksInNewTab?: boolean;
  colorOverride?: string;
  cssOverride?: CSSInterpolation;
  css?: undefined;
};

const bulletPoint = '"\u2022"';

const markdownListStyle = (styling: Styling) =>
  [
    styling.fonts.regular,
    {
      paddingLeft: '1.5rem',
      margin: '0.5rem 0 1rem',
      textAlign: 'left',
      lineHeight: 1.35,
      color: 'inherit',
    },
  ] as CSSInterpolation;

const markdownTextStyle = (styling: Styling, colorOverride?: string) =>
  [
    styling.fonts.regular,
    {
      strong: styling.fonts.bold,
      p: {
        margin: '1rem 0',
      },
      ul: [
        markdownListStyle(styling),
        {
          listStyle: 'none',
          li: {
            '::before': {
              content: bulletPoint,
              display: 'inline-block',
              color: styling.colors.highlightText,
              width: '1.1em',
              marginLeft: '-1.1em',
            },
          },
        },
      ],
      ol: markdownListStyle(styling),
      a: [styling.common.link, { color: colorOverride }],
      color: colorOverride || styling.colors.bodyText,
    },
  ] as CSSInterpolation;

const urlTransform: UrlTransform = (url, key, node) => {
  if (node.tagName === 'a' && key === 'href') {
    return url;
  }

  return parseIfImageUrl(url);
};

export const Markdown: React.FC<MarkdownProps> = ({
  markdownText,
  openLinksInNewTab,
  colorOverride,
  cssOverride,
}) => (
  <SiteConsumer>
    {({ styling }) => (
      <ReactMarkdown
        css={[markdownTextStyle(styling, colorOverride), cssOverride]}
        urlTransform={urlTransform}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          [rehypeExternalLinks, { target: !!openLinksInNewTab ? '_blank' : '_self' }],
        ]}
      >
        {markdownText}
      </ReactMarkdown>
    )}
  </SiteConsumer>
);

export default Markdown;
